/**
 * Organization Switcher Component
 * 
 * Dropdown for switching between organizations when user has multi-org access.
 * Shows current org and allows quick switching.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Check, Plus, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { toast } from 'sonner';

const OrganizationSwitcher = ({ isCollapsed = false, className }) => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await api.get('/api/auth/my-organizations');
        const orgs = response.data.organizations || [];
        setOrganizations(orgs);
        setCurrentOrg(orgs.find(o => o.is_current) || orgs[0]);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const handleSwitchOrg = async (orgId) => {
    if (currentOrg?.organization_id === orgId) return;
    
    setSwitching(true);
    try {
      await api.post('/api/auth/switch-organization', { organization_id: orgId });
      toast.success('Switched organization');
      // Reload the page to refresh all data with new org context
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
      toast.error('Failed to switch organization');
      setSwitching(false);
    }
  };

  const handleCreateOrg = () => {
    navigate('/onboarding/create-org');
  };

  // Don't show if only one org
  if (!loading && organizations.length <= 1) {
    return null;
  }

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon"
            className={cn("w-10 h-10", className)}
          >
            <Building2 className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <div className="px-2 py-1.5 text-sm font-medium text-gray-500">
            Switch Organization
          </div>
          <DropdownMenuSeparator />
          {organizations.map((org) => (
            <DropdownMenuItem
              key={org.organization_id}
              onClick={() => handleSwitchOrg(org.organization_id)}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <span className="truncate">{org.name}</span>
                {org.is_current && <Check className="w-4 h-4 text-blue-600 ml-2" />}
              </div>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleCreateOrg} className="cursor-pointer">
            <Plus className="w-4 h-4 mr-2" />
            Create New Organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className={cn(
            "w-full justify-between text-left font-normal",
            className
          )}
          disabled={switching}
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="w-4 h-4 shrink-0" />
            <span className="truncate">{currentOrg?.name || 'Select Org'}</span>
          </div>
          {switching ? (
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <div className="px-2 py-1.5 text-sm font-medium text-gray-500">
          Switch Organization
        </div>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.organization_id}
            onClick={() => handleSwitchOrg(org.organization_id)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="truncate">{org.name}</span>
              </div>
              {org.is_current && <Check className="w-4 h-4 text-blue-600" />}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleCreateOrg} className="cursor-pointer">
          <Plus className="w-4 h-4 mr-2 text-green-600" />
          Create New Organization
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrganizationSwitcher;
